import { useState, useMemo } from 'react';
import type { EmailTemplate, TemplateSortOption, TemplateFilterOption } from '../types';

interface UseTemplateFilterSortProps {
  templates: EmailTemplate[];
}

export function useTemplateFilterSort({ templates }: UseTemplateFilterSortProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<TemplateSortOption>('recently_updated');
  const [filterType, setFilterType] = useState<TemplateFilterOption>('all');

  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      const matchesSearch =
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.body.toLowerCase().includes(searchTerm.toLowerCase());

      const hasVariables = template.subject.includes('{{') || template.body.includes('{{');

      if (filterType === 'variables') {
        return matchesSearch && hasVariables;
      }
      if (filterType === 'no_variables') {
        return matchesSearch && !hasVariables;
      }
      return matchesSearch;
    });
  }, [templates, searchTerm, filterType]);

  const sortedTemplates = useMemo(() => {
    return [...filteredTemplates].sort((a, b) => {
      if (sortBy === 'recently_updated') {
        const dateA = new Date(a.updated_at || a.created_at).getTime();
        const dateB = new Date(b.updated_at || b.created_at).getTime();
        return dateB - dateA;
      }
      if (sortBy === 'oldest') {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateA - dateB;
      }
      if (sortBy === 'name_asc') {
        return a.name.localeCompare(b.name);
      }
      if (sortBy === 'name_desc') {
        return b.name.localeCompare(a.name);
      }
      return 0;
    });
  }, [filteredTemplates, sortBy]);

  const clearFilters = () => {
    setSearchTerm('');
    setFilterType('all');
  };

  return {
    searchTerm,
    setSearchTerm,
    sortBy,
    setSortBy,
    filterType,
    setFilterType,
    filteredTemplates,
    sortedTemplates,
    clearFilters,
  };
}
